import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

// ===================== PLANS & MARKETPLACE (public lists) =====================

export const listInvestPlans = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { data, error } = await context.supabase
      .from("invest_plans")
      .select("*")
      .eq("is_active", true)
      .order("tenure_months", { ascending: true });
    if (error) throw new Error(error.message);
    return data ?? [];
  });

export const listMarketplace = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { data, error } = await context.supabase
      .from("loan_listings")
      .select("*")
      .eq("is_open", true)
      .order("created_at", { ascending: false });
    if (error) throw new Error(error.message);
    return data ?? [];
  });

// ===================== WALLET =====================

async function getBalance(supabase: any, userId: string): Promise<number> {
  const { data, error } = await supabase
    .from("wallet_transactions")
    .select("balance_after")
    .eq("user_id", userId)
    .order("created_at", { ascending: false })
    .limit(1)
    .maybeSingle();
  if (error) throw new Error(error.message);
  return data ? Number(data.balance_after) : 0;
}

export const getWalletState = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const balance = await getBalance(context.supabase, context.userId);
    const { data: txs, error } = await context.supabase
      .from("wallet_transactions")
      .select("*")
      .eq("user_id", context.userId)
      .order("created_at", { ascending: false })
      .limit(50);
    if (error) throw new Error(error.message);
    return { balance, transactions: txs ?? [] };
  });

const moveFundsInput = z.object({
  amount: z.number().positive().max(10_000_000),
  kind: z.enum(["deposit", "withdraw"]),
  reference: z.string().max(120).optional(),
});

export const moveWalletFunds = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) => moveFundsInput.parse(data))
  .handler(async ({ data, context }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: row, error } = await supabaseAdmin.rpc("record_wallet_transaction", {
      _user_id: context.userId,
      _kind: data.kind,
      _amount: data.amount,
      _reference: data.reference ?? undefined,
      _notes: data.kind === "deposit" ? "Bank transfer (simulated)" : "Withdrawal request",
    });
    if (error) {
      if (error.message?.includes("insufficient_balance")) {
        throw new Error("Insufficient wallet balance");
      }
      throw new Error(error.message);
    }
    return { balance: Number((row as { balance_after: number }).balance_after) };
  });

// ===================== INVESTMENTS =====================

const investInput = z.object({
  planId: z.string().uuid(),
  amount: z.number().positive(),
});

export const createInvestment = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) => investInput.parse(data))
  .handler(async ({ data, context }) => {
    const { data: plan, error: pErr } = await context.supabase
      .from("invest_plans")
      .select("*")
      .eq("id", data.planId)
      .eq("is_active", true)
      .maybeSingle();
    if (pErr) throw new Error(pErr.message);
    if (!plan) throw new Error("Plan not found");

    if (data.amount < Number(plan.min_amount) || data.amount > Number(plan.max_amount)) {
      throw new Error(`Amount must be between ₹${plan.min_amount} and ₹${plan.max_amount}`);
    }

    const balance = await getBalance(context.supabase, context.userId);
    if (balance < data.amount) throw new Error("Insufficient wallet balance. Add funds first.");

    const rate = Number(plan.annual_rate) / 100;
    const years = plan.tenure_months / 12;
    const expectedReturn = +(data.amount * Math.pow(1 + rate, years)).toFixed(2);

    const start = new Date();
    const maturity = new Date(start);
    maturity.setMonth(maturity.getMonth() + plan.tenure_months);

    const { data: inv, error } = await context.supabase
      .from("investments")
      .insert({
        user_id: context.userId,
        plan_id: plan.id,
        amount: data.amount,
        annual_rate: plan.annual_rate,
        tenure_months: plan.tenure_months,
        maturity_date: maturity.toISOString().slice(0, 10),
        expected_return: expectedReturn,
      })
      .select()
      .single();
    if (error) throw new Error(error.message);

    // Atomic debit via SECURITY DEFINER RPC (FOR UPDATE lock prevents TOCTOU overdraft).
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: txRow, error: txErr } = await supabaseAdmin.rpc("record_wallet_transaction", {
      _user_id: context.userId,
      _kind: "invest",
      _amount: data.amount,
      _reference: inv.id,
      _notes: `Invested in ${plan.name}`,
    });
    if (txErr) {
      // Compensate: remove the investment row to keep ledger consistent.
      await supabaseAdmin.from("investments").delete().eq("id", inv.id);
      if (txErr.message?.includes("insufficient_balance")) {
        throw new Error("Insufficient wallet balance. Add funds first.");
      }
      throw new Error(txErr.message);
    }
    const newBal = Number((txRow as { balance_after: number }).balance_after);

    return { investment: inv, balance: newBal };
  });

export const listInvestments = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { data, error } = await context.supabase
      .from("investments")
      .select("*, plan:invest_plans(name, slug, payout_frequency, risk_grade)")
      .eq("user_id", context.userId)
      .order("created_at", { ascending: false });
    if (error) throw new Error(error.message);
    return data ?? [];
  });

// ===================== LOAN APPLICATIONS =====================

const loanAppInput = z.object({
  amount: z.number().positive().max(10_000_000),
  tenureMonths: z.number().int().min(3).max(84),
  purpose: z.string().trim().min(2).max(120),
  monthlyIncome: z.number().positive().max(10_000_000),
  employment: z.enum(["Salaried", "Self-employed", "Business", "Student", "Other"]),
  city: z.string().trim().max(80).optional(),
});

function calcEmi(principal: number, annualRatePct: number, months: number): number {
  const r = annualRatePct / 12 / 100;
  if (r === 0) return principal / months;
  const emi = (principal * r * Math.pow(1 + r, months)) / (Math.pow(1 + r, months) - 1);
  return +emi.toFixed(2);
}

export const createLoanApplication = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) => loanAppInput.parse(data))
  .handler(async ({ data, context }) => {
    // Offered rate driven by income-to-EMI ratio (simple heuristic)
    const baseRate = 14;
    const incomeFactor = Math.min(4, (data.monthlyIncome * 12) / data.amount);
    const offeredRate = +(baseRate + (4 - incomeFactor) * 1.5).toFixed(2);
    const emi = calcEmi(data.amount, offeredRate, data.tenureMonths);

    const { data: row, error } = await context.supabase
      .from("loan_applications")
      .insert({
        user_id: context.userId,
        amount: data.amount,
        tenure_months: data.tenureMonths,
        purpose: data.purpose,
        monthly_income: data.monthlyIncome,
        employment: data.employment,
        city: data.city ?? null,
        offered_rate: offeredRate,
        emi,
        status: "under_review",
      })
      .select()
      .single();
    if (error) throw new Error(error.message);
    return row;
  });

export const listLoanApplications = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { data, error } = await context.supabase
      .from("loan_applications")
      .select("*")
      .eq("user_id", context.userId)
      .order("created_at", { ascending: false });
    if (error) throw new Error(error.message);
    return data ?? [];
  });

// ===================== KYC =====================

const kycInput = z.object({
  fullName: z.string().trim().min(2).max(120),
  pan: z.string().trim().regex(/^[A-Z]{5}[0-9]{4}[A-Z]$/, "Invalid PAN format"),
  aadhaarLast4: z.string().trim().regex(/^[0-9]{4}$/, "Last 4 digits required"),
  dateOfBirth: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  addressLine: z.string().trim().min(4).max(200),
  city: z.string().trim().min(2).max(80),
  state: z.string().trim().min(2).max(80),
  pincode: z.string().trim().regex(/^[0-9]{6}$/),
});

export const getKyc = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { data, error } = await context.supabase
      .from("kyc_submissions")
      .select("*")
      .eq("user_id", context.userId)
      .maybeSingle();
    if (error) throw new Error(error.message);
    return data;
  });

export const upsertKyc = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) => kycInput.parse(data))
  .handler(async ({ data, context }) => {
    const payload = {
      user_id: context.userId,
      full_name: data.fullName,
      pan: data.pan,
      aadhaar_last4: data.aadhaarLast4,
      date_of_birth: data.dateOfBirth,
      address_line: data.addressLine,
      city: data.city,
      state: data.state,
      pincode: data.pincode,
      status: "pending" as const,
    };
    const { data: row, error } = await context.supabase
      .from("kyc_submissions")
      .upsert(payload, { onConflict: "user_id" })
      .select()
      .single();
    if (error) throw new Error(error.message);
    return row;
  });
